// #71: Per-user starred projects. Reads from the generic UserPreference store
// (key='starred_projects') and exposes star/unstar with optimistic updates.

import { computed } from 'vue'
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import { api } from '@/lib/api'
import { useAuthStore } from '@/stores/auth'

const QK = ['user-pref', 'starred_projects'] as const

export function useStarredProjects() {
  const qc = useQueryClient()
  const auth = useAuthStore()

  const query = useQuery({
    queryKey: QK,
    queryFn: () => api.users.getPreference<{ projectIds?: string[] }>('starred_projects'),
    // Gate on authenticated state: AppLayout mounts before auth.init() resolves,
    // so without this the query fires with no/stale token, 401s, triggers a
    // refresh attempt, retries, and can spiral when the session is in flux.
    enabled: computed(() => auth.isAuthenticated),
    retry: false,
    staleTime: 60_000,
  })

  const starredIds = computed<string[]>(() => {
    const v = query.data.value?.value
    return Array.isArray(v?.projectIds) ? v!.projectIds : []
  })

  const isStarred = (projectId: string) => starredIds.value.includes(projectId)

  const starMutation = useMutation({
    mutationFn: (projectId: string) => api.users.starProject(projectId),
    onSuccess: (data) => {
      if (data.projectIds) {
        qc.setQueryData(QK, { value: { projectIds: data.projectIds } })
      } else {
        qc.invalidateQueries({ queryKey: QK })
      }
    },
  })

  const unstarMutation = useMutation({
    mutationFn: (projectId: string) => api.users.unstarProject(projectId),
    onSuccess: (data) => {
      qc.setQueryData(QK, { value: { projectIds: data.projectIds } })
    },
  })

  function toggle(projectId: string) {
    if (isStarred(projectId)) unstarMutation.mutate(projectId)
    else starMutation.mutate(projectId)
  }

  return { starredIds, isStarred, toggle, isLoading: query.isLoading }
}
